import React, { useCallback, useEffect, useState } from 'react';
import { LoginFormButton } from '@/components/LoginForm/LoginFormButton';
import { LoginFormTextField } from '@/components/LoginForm/LoginFormTextField';
import { CloudId } from '@/models/domain/CloudId';
import { LoginId } from '@/models/domain/LoginId';
import { Password } from '@/models/domain/Password';

export type NextAccountLoginFormValues = {
    cloudId?: CloudId;
    loginId?: LoginId;
    password?: Password;
    isDisplayedCloudId?: boolean;
};

type Props = NextAccountLoginFormValues & {
    shouldShowCloudId?: boolean;
    onClickLoginButton: (nextAccountLoginFormProps: NextAccountLoginFormValues) => void;
};

export const NextAccountLoginForm: React.FC<Props> = (props) => {
    const {
        cloudId: inputCloudId,
        loginId: inputLoginId,
        password: inputPassword,
        shouldShowCloudId: stateShouldShowCloudId,
        onClickLoginButton,
    } = props;
    // fixme 本来は以下をstateで管理するのが理想
    const [cloudId, setCloudId] = useState<CloudId | undefined>(inputCloudId);
    const [loginId, setLoginId] = useState<LoginId | undefined>(inputLoginId);
    const [password, setPassword] = useState<Password | undefined>(inputPassword);
    const [cloudIdErrorMessage, setCloudIdErrorMessage] = useState<string>('');
    const [loginIdErrorMessage, setLoginIdErrorMessage] = useState<string>('');
    const [passwordErrorMessage, setPasswordErrorMessage] = useState<string>('');
    const [shouldShowCloudId, setShouldShowCloudId] = useState<boolean>(false);

    useEffect(() => {
        // propsで渡ってくるcloudId等はusecase経由でrepositoryから取得しており、
        // 取得できたら変更される事があるため、変化があったら更新を行う。
        setCloudId(inputCloudId);
        setLoginId(inputLoginId);
        setPassword(inputPassword);
    }, [inputCloudId, inputLoginId, inputPassword]);

    const onInputCloudId = useCallback((inputValue: string) => {
        setCloudId(new CloudId({ value: inputValue }));
    }, []);

    const onChangeCloudId = useCallback((inputValue: string) => {
        const result = CloudId.RULES.find((rule) => {
            return rule(inputValue) !== true;
        });

        if (result && inputValue) {
            setCloudIdErrorMessage(result(inputValue) as string);
            setShouldShowCloudId(true);
        } else {
            setCloudIdErrorMessage('');
        }
    }, []);

    const onInputLoginId = useCallback((inputValue: string) => {
        setLoginId(new LoginId({ value: inputValue }));
    }, []);

    const onChangeLoginId = useCallback((inputValue: string) => {
        const result = LoginId.RULES.find((rule) => {
            return rule(inputValue) !== true;
        });

        if (result && inputValue) {
            setLoginIdErrorMessage(result(inputValue) as string);
        } else {
            setLoginIdErrorMessage('');
        }
    }, []);

    const onInputPassword = useCallback((inputValue: string) => {
        setPassword(new Password({ value: inputValue }));
    }, []);

    const onChangePassword = useCallback((inputValue: string) => {
        const result = Password.RULES.find((rule) => {
            return rule(inputValue) !== true;
        });

        if (result && inputValue) {
            setPasswordErrorMessage(result(inputValue) as string);
        } else {
            setPasswordErrorMessage('');
        }
    }, []);

    const handleClickLoginButton = useCallback(() => {
        // 設定値のバリデーションメッセージを格納する
        if (cloudId) {
            setCloudIdErrorMessage(cloudId.validationMessage);
            if (cloudId.validationMessage) {
                setShouldShowCloudId(true);
            }
        }

        if (loginId) {
            setLoginIdErrorMessage(loginId.validationMessage);
        }

        if (password) {
            setPasswordErrorMessage(password.validationMessage);
        }

        // 設定値のバリデーションチェックを行う
        if (
            !cloudId ||
            !cloudId.isValid ||
            !cloudId.value ||
            !loginId ||
            !loginId.isValid ||
            !loginId.value ||
            !password ||
            !password.isValid ||
            !password.value
        ) {
            return;
        }

        onClickLoginButton({
            cloudId,
            loginId,
            password,
            isDisplayedCloudId: shouldShowCloudId,
        });
    }, [cloudId, loginId, onClickLoginButton, password, shouldShowCloudId]);

    const handleKeydown = useCallback(
        async (e) => {
            if (e.key === 'Enter') {
                handleClickLoginButton();
            }
        },
        [handleClickLoginButton],
    );

    return (
        <div>
            {(stateShouldShowCloudId || shouldShowCloudId) && (
                <LoginFormTextField
                    errorMessage={cloudIdErrorMessage}
                    keepMessageSpace
                    label="クラウドID"
                    value={cloudId?.value || ''}
                    onChange={onChangeCloudId}
                    onInput={onInputCloudId}
                    onKeyDown={handleKeydown}
                />
            )}
            <LoginFormTextField
                errorMessage={loginIdErrorMessage}
                keepMessageSpace
                label="ログインID"
                value={loginId?.value || ''}
                onChange={onChangeLoginId}
                onInput={onInputLoginId}
                onKeyDown={handleKeydown}
            />
            <LoginFormTextField
                errorMessage={passwordErrorMessage}
                keepMessageSpace
                label="パスワード"
                password
                value={password?.value || ''}
                onChange={onChangePassword}
                onInput={onInputPassword}
                onKeyDown={handleKeydown}
            />
            <LoginFormButton onClick={handleClickLoginButton}>ログイン</LoginFormButton>
        </div>
    );
};
